import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../../context/AppContext';
import { validateFile, formatFileSize } from '../../utils/fileUtils';
import { loadPDF } from '../../utils/pdfUtils';
import './FileUpload.css';

const FileUpload = () => {
  const { settings, addFile, showNotification, setCurrentView } = useApp();
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setIsUploading(true);

    for (const file of acceptedFiles) {
      const validation = validateFile(file, settings.maxFileSize);

      if (!validation.valid) {
        showNotification(validation.error, 'error');
        continue;
      }

      try {
        const pdfDoc = await loadPDF(file);
        addFile(file, pdfDoc);
        showNotification(`${file.name} uploaded successfully`, 'success');
      } catch (error) {
        console.error('Error loading PDF:', error);
        showNotification(`Failed to load ${file.name}`, 'error');
      }
    }

    setIsUploading(false);
  }, [settings.maxFileSize, addFile, showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,
    disabled: isUploading
  });

  return (
    <div className="file-upload-container">
      <div className="upload-content-wrapper">
        <div className="upload-header">
          <h1>GoSplitPDF</h1>
          <p>Split, merge, sign, and convert PDF documents - free and secure</p>
        </div>

        <div className="quick-actions">
          <button className="btn btn-secondary" onClick={() => setCurrentView('merge')}>
            Merge PDFs
          </button>
          <button className="btn btn-secondary" onClick={() => setCurrentView('convert')}>
            Convert to PDF
          </button>
        </div>

        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${isUploading ? 'uploading' : ''}`}
        >
          <input {...getInputProps()} />

          {isUploading ? (
            <div className="upload-status">
              <div className="spinner-lg"></div>
              <p>Processing PDF...</p>
            </div>
          ) : (
            <>
              <svg
                className="upload-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>

              <h2>
                {isDragActive ? 'Drop your PDF here' : 'Drag & drop PDF files'}
              </h2>

              <p className="upload-description">
                or <span className="browse-text">browse files</span>
              </p>

              <div className="upload-info">
                <p>Maximum file size: {formatFileSize(settings.maxFileSize)}</p>
                <p>Supports: PDF files only</p>
              </div>
            </>
          )}
        </div>

        <div className="features-grid">
          <button className="feature-card feature-card-link" onClick={() => setCurrentView('merge')}>
            <div className="feature-icon">📑</div>
            <h3>Merge PDFs</h3>
            <p>Combine multiple PDF files into one</p>
            <span className="feature-link-hint">Click to use →</span>
          </button>

          <button className="feature-card feature-card-link" onClick={() => setCurrentView('convert')}>
            <div className="feature-icon">🔄</div>
            <h3>Convert to PDF</h3>
            <p>Convert images and other files to PDF</p>
            <span className="feature-link-hint">Click to use →</span>
          </button>

          <div className="feature-card">
            <div className="feature-icon">✍️</div>
            <h3>Sign Documents</h3>
            <p>Draw or upload your signature to sign PDFs</p>
            <span className="feature-requires">Requires PDF upload</span>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📝</div>
            <h3>Add Text</h3>
            <p>Add text anywhere on your PDF documents</p>
            <span className="feature-requires">Requires PDF upload</span>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🔲</div>
            <h3>Redact Content</h3>
            <p>Black out sensitive information in PDFs</p>
            <span className="feature-requires">Requires PDF upload</span>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📄</div>
            <h3>Split PDF</h3>
            <p>Extract pages or split into multiple files</p>
            <span className="feature-requires">Requires PDF upload</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
